/* Stix PDP Variant Picker — CRO Test Router
 * ------------------------------------------
 * Test ID: pdp_stockaware_picker
 * Arms:    control (60%) / a pills-v2 (10%) / d dropdowns (20%) / e wizard (10%)
 *
 * Responsibilities:
 *   1. Cookie-based variant assignment (sticky per visitor, 30-day TTL).
 *      Cookie name: cl_pdp_picker_v. Values: control | a | d | e.
 *      Override: ?cl_picker_v=a|d|e|control in URL forces an assignment (QA).
 *   2. Set cl_test_id and cl_test_variant as GA4 custom dimensions via
 *      gtag('set'). Per Doc/CRO/CRO_AB_Testing_Plan.md these are
 *      event-scoped GA4 custom dimensions registered in GA4 admin.
 *   3. Set window.STIX_TEST[testId] = variant for downstream consumers
 *      (other tracking calls in main-product.js can pick this up).
 *   4. Activate the matching arm by setting data-cl-active-arm on the
 *      [data-cl-picker-router] element, and toggling visibility of the
 *      control vs treatment markup.
 *
 * Control = the live inline picker in sections/main-product.liquid. For arms
 * a and d, that picker is HIDDEN (display:none) but stays in the DOM —
 * the treatment UI programmatically clicks those underlying radios so the
 * existing onOptionChange handler fires every tracking event unchanged.
 * For arm e, the chip picker stays VISIBLE (wizard is an "additional entry"
 * per the brief, not a replacement).
 */
(function () {
  'use strict';

  var TEST_ID = 'pdp_stockaware_picker';
  var COOKIE_NAME = 'cl_pdp_picker_v2';
  var COOKIE_TTL_DAYS = 30;
  var URL_OVERRIDE_PARAM = 'cl_picker_v';
  var SPLIT = [
    { arm: 'control', weight: 45 },  // 2026-05-26 rev2
    { arm: 'a',       weight: 25 },  // open up arm a (was 0%, +6.9% NS on 869 sess)
    { arm: 'd',       weight: 30 },  // cascading dropdowns (+14.8% NS, leading non-control)
  ];
  var VALID_ARMS = { control: 1, a: 1, d: 1 }; // 2026-05-26 rev2 — kill e (sig loser -47%, p<0.001)

  /* TEST_MODE — gates how visitors get assigned.
     - 'smoke': default for first deploy. No random assignment, no cookie
       write, no exposure event. Everyone sees `control` (the live chip
       picker) unless they explicitly load a URL with ?cl_picker_v=a|d|e.
       Use this to validate each picker UI on the live site without any
       traffic actually being in the test. The CRO dashboards see zero
       new test traffic in this mode.
     - 'live': production test. Random assignment per SPLIT, cookie
       persists 30 days, exposure event fires on PDP load. This is what
       runs the actual A/B/C/D experiment.
     Flip this single constant to start the test. */
  var TEST_MODE = 'live';
  // ─── Metafield override (rev2.5 — Wiki/Ecomm/Reports/cro-weekly-2026-05-26 §arch) ───
  // Read shop.metafields.cro.test_config['pdp_stockaware_picker'] from the page-global
  // emitted by snippets/cro-config-global.liquid. If present, REPLACE the
  // hardcoded SPLIT + VALID below so weight changes via Scripts/cro_config.py
  // take effect on next page load without a theme deploy.
  // Falls back to the hardcoded defaults if the global is missing (dev/preview).
  try {
    var __mf = window.STIX_CRO_CONFIG && window.STIX_CRO_CONFIG[TEST_ID];
    if (__mf && __mf.variants && __mf.variants.length) {
      SPLIT = __mf.variants.map(function(v){ return {arm: v.label, weight: v.weight}; });
      VALID_ARMS = {};
      __mf.variants.forEach(function(v){ VALID_ARMS[v.label] = 1; });
      if (__mf.status === 'paused' || __mf.status === 'reverted') TEST_MODE = 'smoke';
      window.STIX_TEST_SOURCE = window.STIX_TEST_SOURCE || {};
      window.STIX_TEST_SOURCE[TEST_ID] = 'metafield';
    } else {
      window.STIX_TEST_SOURCE = window.STIX_TEST_SOURCE || {};
      window.STIX_TEST_SOURCE[TEST_ID] = 'hardcoded-fallback';
    }
  } catch (e) {
    window.STIX_TEST_SOURCE = window.STIX_TEST_SOURCE || {};
    window.STIX_TEST_SOURCE[TEST_ID] = 'hardcoded-fallback-error';
  }


  function getCookie(name) {
    var rx = new RegExp('(?:^|;\\s*)' + name + '=([^;]*)');
    var m = document.cookie.match(rx);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeCartAttribute(testId, arm) {
    /* Standard CRO attribution: write cro_test_<test_id>=<arm> to the cart so
       it rides into the order. A Shopify Custom Pixel subscribes to
       checkout_completed and fires a cro_purchase GA4 event for each
       cro_test_* attribute on the order. This is the ONLY path that survives
       the storefront → checkout boundary for per-arm revenue attribution.
       Matches the pattern used by every other Stix CRO test. */
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'attributes[cro_test_' + testId + ']=' + encodeURIComponent(arm),
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () { /* silent — cart attr is best-effort */ });
    } catch (e) { /* silent */ }
  }

  function setCookie(name, val, days) {
    var d = new Date();
    d.setTime(d.getTime() + (days * 86400000));
    document.cookie = name + '=' + encodeURIComponent(val) +
      '; expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax';
  }
  function urlOverride() {
    try {
      var p = new URLSearchParams(window.location.search).get(URL_OVERRIDE_PARAM);
      return (p && VALID_ARMS[p]) ? p : null;
    } catch (e) { return null; }
  }
  function assignArm() {
    var r = Math.random() * 100;
    var cum = 0;
    for (var i = 0; i < SPLIT.length; i++) {
      cum += SPLIT[i].weight;
      if (r < cum) return SPLIT[i].arm;
    }
    return SPLIT[0].arm;
  }
  function getOrAssign() {
    var override = urlOverride();
    if (override) {
      /* URL override does NOT persist via cookie — strictly for QA / smoke testing. */
      return { arm: override, source: 'url-override' };
    }
    if (TEST_MODE === 'smoke') {
      /* Smoke mode — no random assignment, no cookie write. Default to control
         so live visitors see the existing chip picker unchanged. Treatments
         only render when explicitly requested via ?cl_picker_v=a|d|e. */
      return { arm: 'control', source: 'smoke-default' };
    }
    var existing = getCookie(COOKIE_NAME);
    if (existing && VALID_ARMS[existing]) {
      writeCartAttribute(TEST_ID, existing);
      return { arm: existing, source: 'cookie' };
    }
    var assigned = assignArm();
    setCookie(COOKIE_NAME, assigned, COOKIE_TTL_DAYS);
    writeCartAttribute(TEST_ID, assigned);
    return { arm: assigned, source: 'new-assignment' };
  }

  function fireExposureEvent(testId, arm, source) {
    /* Standard CRO exposure event — needed so GA4/TW can build a denominator
       per arm. Custom dims set BEFORE the event so they attach to the
       exposure itself. */
    try {
      if (window.gtag) {
        window.gtag('set', { cl_test_id: testId, cl_test_variant: arm });
        window.gtag('event', 'ab_test_exposure', {
          cl_test_id: testId,
          cl_test_variant: arm,
          assignment_source: source
        });
      }
    } catch (e) {}
    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'ab_test_exposure',
        cl_test_id: testId,
        cl_test_variant: arm,
        assignment_source: source
      });
    } catch (e) {}
    try {
      if (window.stix && window.stix.track && window.stix.track.trackEvent) {
        window.stix.track.trackEvent('ab_test_exposure', {
          cl_test_id: testId,
          cl_test_variant: arm,
          assignment_source: source
        });
      }
    } catch (e) {}
  }

  function activate(routerEl, arm) {
    routerEl.setAttribute('data-cl-active-arm', arm);
    /* Also stamp on <html> so CSS can scope rules without depending on DOM hierarchy */
    document.documentElement.setAttribute('data-cl-active-arm', arm);
    routerEl.querySelectorAll('[data-cl-arm]').forEach(function (el) {
      if (el.getAttribute('data-cl-arm') === arm) {
        el.removeAttribute('hidden');
        el.setAttribute('data-cl-active', '');
      } else {
        el.setAttribute('hidden', '');
        el.removeAttribute('data-cl-active');
      }
    });

    /* Treatment arms own the full variant UI + OOS UI. Control leaves the
       theme rendering as-is. Arm E additionally hides the main red ATC
       (.product__cta-wrapper) — the wizard renders its own ATC at the
       final step. */
    var control = document.querySelector('[data-cl-control-picker]');
    var oosBlock = document.querySelector('[data-product-oos-block-container]');
    var ctaWrapper = document.querySelector('.product__cta-wrapper:not([data-sticky-cta])') ||
                     document.querySelector('.product__cta-wrapper');
    if (arm === 'control') {
      if (control) { control.style.display = ''; control.removeAttribute('data-cl-hidden'); }
      /* Only strip the inline display if THIS router previously hid it (i.e.,
         the user was cookie-reassigned from arm A/D/E back to control). Never
         clobber Liquid's server-rendered `style="display:none"`, which is set
         when current_variant.available is true — stripping it exposes the OOS
         block on in-stock variants, since the legacy picker only re-toggles
         display in response to user option-change events, not on initial load.
         (See main-product.js → toggleAddToCartState.) */
      if (oosBlock && oosBlock.hasAttribute('data-cl-hidden')) {
        oosBlock.style.removeProperty('display');
        oosBlock.removeAttribute('data-cl-hidden');
      }
      if (ctaWrapper && ctaWrapper.hasAttribute('data-cl-hidden')) {
        ctaWrapper.style.removeProperty('display');
        ctaWrapper.removeAttribute('data-cl-hidden');
      }
    } else {
      if (control) { control.style.display = 'none'; control.setAttribute('data-cl-hidden', ''); }
      if (oosBlock) {
        oosBlock.style.setProperty('display', 'none', 'important');
        oosBlock.setAttribute('data-cl-hidden', '');
      }
      /* Hide the main red ATC ONLY for arm E (wizard owns its own ATC).
         A and D leave the main ATC visible — they're just better variant
         pickers. */
      if (ctaWrapper && arm === 'e') {
        ctaWrapper.style.setProperty('display', 'none', 'important');
        ctaWrapper.setAttribute('data-cl-hidden', '');
      } else if (ctaWrapper) {
        ctaWrapper.style.removeProperty('display');
      }
    }
  }

  function init() {
    var router = document.querySelector('[data-cl-picker-router]');
    if (!router) return;
    var assignment = getOrAssign();
    var arm = assignment.arm;

    window.STIX_TEST = window.STIX_TEST || {};
    window.STIX_TEST[TEST_ID] = arm;

    /* gtag('set') ASAP so every subsequent event on this page carries the
       test custom dimensions. */
    try {
      if (window.gtag) {
        window.gtag('set', { cl_test_id: TEST_ID, cl_test_variant: arm });
      }
    } catch (e) {}

    activate(router, arm);
    /* Skip exposure event in smoke-default state — no test traffic should
       land in CRO dashboards until TEST_MODE goes live. URL-override sessions
       DO fire the exposure so QA can validate the analytics path. */
    if (assignment.source !== 'smoke-default') {
      fireExposureEvent(TEST_ID, arm, assignment.source);
    }

    /* Expose the arm to the per-arm scripts via custom event */
    document.dispatchEvent(new CustomEvent('cl-picker-router:activated', {
      detail: { arm: arm, testId: TEST_ID, assignment: assignment.source }
    }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
